import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { ReleaseSettings } from "./ReleaseSettings";
import { TutorialOverlay, CINDERWAKE_TUTORIAL_STEPS } from "./TutorialOverlay";
import { applyPreferences, loadPreferences, normalizeSettings, savePreferences } from "../platform/preferences";
import { toggleFullscreen, useGamepadNavigation } from "../platform/controls";
import { gameStorage } from "../platform/storage";
import type { GameSettings } from "../game/types";

const TUTORIAL_KEY = "cinderwake.tutorial.complete.v1";

export function ReleaseHost({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<GameSettings>(() => loadPreferences());
  const [paused, setPaused] = useState(false);
  const [tutorialStep, setTutorialStep] = useState<number | null>(null);
  const updateSettings = useCallback((next: GameSettings) => {
    const normalized = normalizeSettings(next);
    setSettings(normalized);
    savePreferences(normalized);
    applyPreferences(normalized);
    window.dispatchEvent(new CustomEvent<GameSettings>("cinderwake:preferences", { detail: normalized }));
  }, []);
  const togglePause = useCallback(() => setPaused((value) => !value), []);
  useGamepadNavigation(togglePause, settings.controllerHints);
  useEffect(() => { applyPreferences(settings); }, [settings]);
  useEffect(() => { const open = () => setPaused(true); window.addEventListener("cinderwake:open-menu", open); return () => window.removeEventListener("cinderwake:open-menu", open); }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (event.key === "F11") { event.preventDefault(); void toggleFullscreen(); return; }
      if (target?.matches("input, textarea, select, [contenteditable=true]")) return;
      if (event.key === "Escape" || event.key.toLowerCase() === "p") { event.preventDefault(); togglePause(); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [togglePause]);

  useEffect(() => {
    if (!settings.tutorial || tutorialStep !== null || paused) return;
    const check = () => {
      try {
        const hasRun = Boolean(gameStorage.getItem("cinderwake.run.v1"));
        const complete = gameStorage.getItem(TUTORIAL_KEY) === "true";
        if (hasRun && !complete) setTutorialStep(0);
      } catch { /* optional storage */ }
    };
    check();
    window.addEventListener("cinderwake:run-saved", check);
    return () => window.removeEventListener("cinderwake:run-saved", check);
  }, [paused, settings.tutorial, tutorialStep]);

  const finishTutorial = () => {
    try { gameStorage.setItem(TUTORIAL_KEY, "true"); } catch { /* optional storage */ }
    setTutorialStep(null);
  };
  return <>
    <div aria-hidden={paused || tutorialStep !== null ? "true" : undefined}>{children}</div>
    <button className="release-pause-button" onClick={togglePause}>Menu · Esc / Start</button>
    {settings.controllerHints && !paused && tutorialStep === null && <div className="input-hints"><span>D-pad / Stick · Navigate</span><span>A / Enter · Confirm</span><span>B / Esc · Back</span><span>1–9 · Play card</span><span>E · End turn</span></div>}
    {tutorialStep !== null && <TutorialOverlay step={tutorialStep} onSkip={finishTutorial} onNext={() => tutorialStep + 1 >= CINDERWAKE_TUTORIAL_STEPS ? finishTutorial() : setTutorialStep(tutorialStep + 1)} />}
    {paused && <div className="release-overlay pause-overlay" role="dialog" aria-modal="true" aria-label="Cinderwake paused"><section><span className="eyebrow">EXPEDITION PAUSED</span><h2>The Wake holds its breath.</h2><p>Turn-based combat has no timer. Adjust the interface or return when ready.</p><ReleaseSettings settings={settings} onChange={updateSettings} onResetTutorial={() => { try { gameStorage.removeItem(TUTORIAL_KEY); } catch { /* ignored */ } setTutorialStep(0); setPaused(false); }} /><div className="pause-actions"><button className="primary-button" onClick={() => setPaused(false)}>Continue</button><button className="ghost-button" onClick={() => void toggleFullscreen()}>Toggle fullscreen</button></div></section></div>}
  </>;
}
